(function initializeOffer(root, factory) {
  'use strict';
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root?.customElements && !root.customElements.get('alvaura-first-order-offer')) {
    root.customElements.define('alvaura-first-order-offer', api.AlvauraFirstOrderOffer);
  }
})(typeof window === 'object' ? window : null, function createOfferApi(root) {
  'use strict';
  const keys = ['ruleCode', 'publicCodes', 'country', 'currency', 'welcomeLabel', 'planSavingsLabel', 'priceCents',
    'variantId', 'productId', 'blockId', 'sectionId', 'formId', 'available',
    'customerStatus', 'oneTime', 'planRequired', 'planPrices', 'quantityMin', 'compareAtCents'];
  const yes = (value) => value === true || value === 'true';
  const id = (value) => typeof value === 'string' && /^[1-9]\d*$/.test(value) ? value : null;
  const integer = (value) => {
    if (typeof value !== 'number' && (typeof value !== 'string' || !/^\d+$/.test(value))) return null;
    const number = Number(value);
    return Number.isSafeInteger(number) && number >= 0 ? number : null;
  };
  const code = (value) => typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(value);
  const normalizedCode = (value) => String(value).toUpperCase();
  const revisionKey = (value) => JSON.stringify([value.variantId, value.quantity, value.sellingPlanId,
    value.country, value.currency, value.unitCents, value.planCents, value.customerStatus, value.properties, value.basket]);
  let session;
  let sessionConfiguration;
  let disposal;
  let recovery;
  let pageLeaving = false;

  async function request(url, options = {}) {
    const abort = new root.AbortController();
    const timer = root.setTimeout(() => abort.abort(), 10000);
    try {
      const response = await root.fetch(url, {cache: 'no-store', redirect: 'error', ...options, signal: abort.signal});
      const body = await response.text();
      return {ok: response.ok, status: response.status, json: async () => JSON.parse(body), text: async () => body};
    } finally {
      root.clearTimeout(timer);
    }
  }

  function getSession(data) {
    if (pageLeaving || recovery || !root?.AlvauraOfferQuote?.createSession || !code(data.ruleCode)) return null;
    let publicCodes;
    try { publicCodes = JSON.parse(data.publicCodes); } catch (_) { return null; }
    if (!Array.isArray(publicCodes) || publicCodes.length > 5 || !publicCodes.every(code)) return null;
    const configuration = JSON.stringify([data.ruleCode, publicCodes]);
    if (session && configuration !== sessionConfiguration) return null;
    if (!session) {
      session = root.AlvauraOfferQuote.createSession(request, '/api/2026-07/graphql.json', {
        welcomeCode: data.ruleCode, publicCodes
      });
      sessionConfiguration = configuration;
    }
    return session;
  }

  async function waitForRecovery() {
    const pending = recovery;
    if (!pending) return;
    const ready = await pending;
    const error = new Error(ready ? 'QUOTE_RECOVERY_READY' : 'QUOTE_RECOVERY_UNCONFIRMED');
    error.code = error.message;
    error.quoteRecovered = ready === true && !pageLeaving;
    throw error;
  }

  async function quoteOffer(data, context) {
    if (recovery) await waitForRecovery();
    const owned = getSession(data);
    if (!owned) return null;
    try {
      return await owned.quote(context);
    } catch (error) {
      if (recovery && session === owned && ['SESSION_FAILED', 'SESSION_DISPOSED'].includes(error?.code)) await waitForRecovery();
      if (!pageLeaving && session === owned &&
        ['BASE_PRICE_MISMATCH', 'QUOTE_CHANGED', 'SELLING_PLAN_PRICE_CHANGED', 'QUOTE_REQUEST_FAILED', 'SESSION_FAILED'].includes(error?.code)) {
        if (!recovery) {
          const pending = Promise.resolve().then(() => owned.dispose()).then((result) => {
            if (result?.cleaned !== true || session !== owned || pageLeaving) return false;
            session = null;
            sessionConfiguration = null;
            return true;
          }).catch(() => false);
          recovery = pending;
          pending.finally(() => { if (recovery === pending) recovery = null; });
        }
        if (await recovery) error.quoteRecovered = true;
      }
      throw error;
    }
  }

  function evaluate(input = {}, quote) {
    const quantity = integer(input.quantity);
    const price = integer(input.priceCents);
    const selectedPlan = input.sellingPlan === '' ? '' : id(input.sellingPlan);
    let planCents = price;
    let planValid = selectedPlan === '' && yes(input.oneTime) && !yes(input.planRequired);
    if (selectedPlan) {
      try {
        const plans = JSON.parse(input.planPrices);
        const matches = Array.isArray(plans) && plans.length <= 50 ? plans.filter((plan) => plan.id === selectedPlan) : [];
        const plan = matches.length === 1 ? matches[0] : null;
        planCents = integer(plan?.priceCents);
        planValid = planCents > 0 && planCents <= price && integer(plan?.compareCents) === price;
      } catch (_) { planValid = false; }
    }
    const valid = yes(input.formKnown) && !input.pending && yes(input.available) && planValid &&
      id(input.variantId) && input.formVariantId === input.variantId &&
      ['guest', 'new', 'returning'].includes(input.customerStatus) &&
      /^[A-Z]{2}$/.test(input.country || '') && /^[A-Z]{3}$/.test(input.currency || '') &&
      quantity > 0 && quantity <= 1000 && integer(input.quantityMin) === 1 && price > 0 &&
      Number.isSafeInteger(price * quantity);
    if (!valid) return {showBadge: false, showEstimate: false, context: null};
    const context = {variantId: input.variantId, quantity, sellingPlanId: selectedPlan, country: input.country,
      currency: input.currency, unitCents: price, planCents, customerStatus: input.customerStatus};
    if (input.properties?.length) context.properties = input.properties;
    const basketSubtotal = input.basket?.lines?.reduce((sum, line) => sum + line.unitCents * line.quantity, 0);
    const compareAt = integer(input.compareAtCents);
    const welcomePriceAllowed = normalizedCode(quote?.code) !== normalizedCode(input.ruleCode) || compareAt !== null && compareAt <= price;
    const matched = quote && welcomePriceAllowed && quote.quantity === quantity && quote.currency === input.currency &&
      quote.subtotalCents === (input.basket ? basketSubtotal : price * quantity) && quote.discounted === true &&
      (input.basket ? quote.basket === true && quote.targetDiscounted === true : !selectedPlan || quote.planSubtotalCents === planCents * quantity) &&
      Number.isSafeInteger(quote.totalCents) && quote.totalCents >= 0 && quote.totalCents < quote.subtotalCents &&
      !(input.customerStatus === 'returning' && normalizedCode(quote.code) === normalizedCode(input.ruleCode));
    return {context, showBadge: Boolean(matched), showEstimate: Boolean(matched && quantity === 1 && (!input.basket ||
      quote.singleTarget === true && input.basket.lines.length === 1 && input.basket.lines[0].quantity === 1))};
  }

  function cartCodes(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return null;
    if (cart.discount_codes.some((entry) => !entry || !code(entry.code) || typeof entry.applicable !== 'boolean')) return null;
    return cart.discount_codes.map((entry) => ({code: entry.code, applicable: entry.applicable}));
  }

  function matchingQuantity(cart, context) {
    if (!cart || cart.currency !== context.currency || !Array.isArray(cart.items) || cartCodes(cart) === null ||
      !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 || cart.item_count > 1000 || cart.items.length > 100) return null;
    let quantity = 0;
    for (const item of cart.items) {
      const allocation = item.selling_plan_allocation;
      const plan = allocation ? String(allocation.selling_plan?.id || '') : '';
      if (allocation && !id(plan)) return null;
      if (String(item.variant_id ?? item.id) !== context.variantId || plan !== context.sellingPlanId ||
        !Number.isSafeInteger(item.quantity) || item.quantity <= 0 ||
        item.original_line_price !== (context.planCents ?? context.unitCents) * item.quantity) return null;
      if (plan && (allocation.price !== context.planCents || allocation.compare_at_price !== context.unitCents)) return null;
      quantity += item.quantity;
    }
    return quantity === cart.item_count ? quantity : null;
  }

  function cartMatches(cart, context, allowEmpty = false) {
    const quantity = matchingQuantity(cart, context);
    return quantity !== null && ((allowEmpty && quantity === 0) || quantity === context.quantity);
  }

  function attributes(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const entries = Object.entries(value);
    if (entries.length > 250 || entries.some(([key, value]) => !key || key.length > 255 || typeof value !== 'string' || value.length > 10000)) return null;
    return entries.filter(([, value]) => value !== '').map(([key, value]) => ({key, value})).sort((left, right) => left.key.localeCompare(right.key));
  }

  function propertiesFromData(formData) {
    if (!formData?.entries) return [];
    const values = Object.create(null);
    for (const [name, value] of formData.entries()) {
      if (['parent_id', 'parent_line_id'].includes(name) && value) return null;
      const match = /^properties\[(.+)\]$/.exec(name);
      if (!match) continue;
      if (typeof value !== 'string') return null;
      values[match[1]] = value;
    }
    return attributes(values);
  }

  function propertiesFromForm(form) {
    if (!form?.elements?.[Symbol.iterator]) return [];
    const entries = [];
    for (const field of form.elements) {
      if (field.disabled || !/^properties\[.+\]$/.test(field.name || '') || !['INPUT', 'SELECT', 'TEXTAREA'].includes(field.tagName)) continue;
      if (['submit', 'button', 'reset', 'image'].includes(field.type) || (['checkbox', 'radio'].includes(field.type) && !field.checked)) continue;
      if (field.type === 'file' || field.multiple || typeof field.value !== 'string') return null;
      entries.push([field.name, field.value]);
    }
    return propertiesFromData({entries: () => entries[Symbol.iterator]()});
  }

  function lineKey(line) {
    return JSON.stringify([line.variantId, line.sellingPlanId, line.attributes]);
  }

  function nativeBasket(cart, context) {
    const codes = cartCodes(cart);
    const cartAttributes = attributes(cart?.attributes);
    if (!cart || cart.currency !== context.currency || !cartAttributes || !codes || codes.length > 5 ||
      !Array.isArray(cart.items) || cart.items.length > 100 || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 || cart.item_count > 1000 ||
      (cart.note != null && typeof cart.note !== 'string') || (cart.note?.length || 0) > 10000) return null;
    const lines = [];
    let quantity = 0;
    for (const item of cart.items) {
      if (item.parent_relationship || item.parent_id || item.has_components || item.item_components?.length || item.components?.length) return null;
      const properties = attributes(item.properties);
      const variantId = id(String(item.variant_id ?? item.id));
      const allocation = item.selling_plan_allocation;
      const sellingPlanId = allocation ? id(String(allocation.selling_plan?.id || '')) : '';
      if (!properties || !variantId || (allocation && !sellingPlanId) || !Number.isSafeInteger(item.quantity) || item.quantity <= 0 ||
        !Number.isSafeInteger(item.original_line_price) || item.original_line_price <= 0 || item.original_line_price % item.quantity !== 0) return null;
      const planCents = item.original_line_price / item.quantity;
      const unitCents = allocation ? integer(allocation.compare_at_price) : planCents;
      if (!unitCents || unitCents < planCents || (allocation && allocation.price !== planCents)) return null;
      const line = {variantId, sellingPlanId, quantity: item.quantity, unitCents, planCents, attributes: properties};
      const same = lines.find(candidate => lineKey(candidate) === lineKey(line));
      if (same) {
        if (same.unitCents !== unitCents || same.planCents !== planCents) return null;
        same.quantity += line.quantity;
      } else lines.push(line);
      quantity += item.quantity;
    }
    if (quantity !== cart.item_count) return null;
    lines.sort((left, right) => lineKey(left).localeCompare(lineKey(right)));
    return {lines, attributes: cartAttributes, note: cart.note || '', codes};
  }

  function basketShape(basket) {
    return JSON.stringify([basket?.lines?.map(line => [line.variantId, line.sellingPlanId, line.quantity, line.unitCents, line.planCents, line.attributes]), basket?.attributes, basket?.note]);
  }

  function basketMatches(cart, context) {
    const basket = nativeBasket(cart, context);
    return Boolean(basket && basketShape(basket) === basketShape(context.basket));
  }

  function purchaseContext(cart, context) {
    const existing = matchingQuantity(cart, context);
    const quantity = existing === null ? null : existing + context.quantity;
    const extra = Boolean(context.properties?.length || Object.keys(cart?.attributes || {}).length || cart?.note || cart?.items?.some(item => Object.keys(item.properties || {}).length));
    if (quantity > 0 && quantity <= 1000 && Number.isSafeInteger(context.unitCents * quantity) && !extra) return {...context, quantity};
    const basket = nativeBasket(cart, context);
    if (!basket || cart.item_count + context.quantity > 1000) return null;
    const target = {variantId: context.variantId, sellingPlanId: context.sellingPlanId, attributes: context.properties || []};
    const same = basket.lines.find(line => lineKey(line) === lineKey(target));
    if (same) {
      if (same.unitCents !== context.unitCents || same.planCents !== context.planCents) return null;
      same.quantity += context.quantity;
    } else basket.lines.push({...target, quantity: context.quantity, unitCents: context.unitCents, planCents: context.planCents});
    basket.lines.sort((left, right) => lineKey(left).localeCompare(lineKey(right)));
    basket.targetAttributes = target.attributes;
    return {...context, basket};
  }

  function sameCodeNames(left, right) {
    const names = cart => cartCodes(cart)?.map(entry => normalizedCode(entry.code)).sort();
    return JSON.stringify(names(left)) === JSON.stringify(names(right));
  }

  function basketCodesPreserved(cart, offer) {
    const existing = cartCodes(cart);
    return Boolean(existing && Array.isArray(offer.applyCodes) && Array.isArray(offer.codeStates) &&
      existing.every(entry => offer.applyCodes.some(code => normalizedCode(code) === normalizedCode(entry.code)) &&
        (!entry.applicable || offer.codeStates.some(candidate => candidate.applicable && normalizedCode(candidate.code) === normalizedCode(entry.code)))));
  }

  function sameCodes(left, right) {
    const codes = (cart) => cartCodes(cart)?.map((entry) => [normalizedCode(entry.code), entry.applicable]).sort();
    return JSON.stringify(codes(left)) === JSON.stringify(codes(right));
  }

  function preservesCodes(cart, selectedCode) {
    const current = cartCodes(cart);
    return current !== null && current.every((entry) => selectedCode && normalizedCode(entry.code) === normalizedCode(selectedCode));
  }

  function formatMoney(cents, currency, usdFace = false) {
    const value = cents / 100;
    if (usdFace) return 'US$' + new Intl.NumberFormat('en-US', {minimumFractionDigits: cents % 100 ? 2 : 0,
      maximumFractionDigits: 2}).format(value);
    return new Intl.NumberFormat(root?.document?.documentElement?.lang || 'en', {
      style: 'currency', currency, currencyDisplay: 'narrowSymbol'
    }).format(value);
  }

  class AlvauraFirstOrderOffer extends (root?.HTMLElement || class {}) {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.pending = false;
      this.sourceValid = true;
      this.sequence = this.sequence || 0;
      this.cartRefreshQueued = false;
      this.priceHost = this.closest('product-price.alvaura-first-order-price');
      this.productInfo = this.closest('product-info');
      this.document = this.ownerDocument;
      this.variantEvent = root?.MainEvents?.variantUpdate || 'variant:update';
      this.onInput = (event) => {
        const target = event.target;
        const form = this.mainForm();
        const variantSelection = Boolean(this.productInfo?.contains(target) && target?.closest?.('variant-selects'));
        if (variantSelection) this.pending = true;
        if (variantSelection || this.productInfo?.contains(target) || (form && (target?.form === form || form.contains(target)))) {
          this.invalidate();
          this.schedule();
        }
      };
      this.onVariantUpdate = (event) => this.syncVariant(event);
      this.onActivity = () => {
        this.invalidate();
        if (!this.document.hidden) this.schedule(true);
      };
      this.onCartUpdate = () => {
        if (!this.connected) return;
        this.invalidate();
        this.cartRefreshQueued = true;
        this.flushCartRefresh();
      };
      this.document.addEventListener('input', this.onInput);
      this.document.addEventListener('change', this.onInput);
      this.document.addEventListener(this.variantEvent, this.onVariantUpdate);
      this.document.addEventListener('visibilitychange', this.onActivity);
      this.document.addEventListener('cart:updated', this.onCartUpdate);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS?.cartUpdate) {
        this.cartUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      }
      root.addEventListener('focus', this.onActivity);
      this.onResize = () => this.preserveLayout();
      root.addEventListener('resize', this.onResize);
      this.observeFormContext();
      this.refresh();
    }

    disconnectedCallback() {
      this.connected = false;
      this.invalidate();
      root.clearTimeout(this.inputTimer);
      root.clearTimeout(this.contextTimer);
      this.cartRefreshQueued = false;
      this.document?.removeEventListener('cart:updated', this.onCartUpdate);
      try { this.cartUnsubscriber?.(); } catch (_) {}
      this.cartUnsubscriber = null;
      this.document?.removeEventListener('input', this.onInput);
      this.document?.removeEventListener('change', this.onInput);
      this.document?.removeEventListener(this.variantEvent, this.onVariantUpdate);
      this.document?.removeEventListener('visibilitychange', this.onActivity);
      root.removeEventListener('focus', this.onActivity);
      root.removeEventListener('resize', this.onResize);
    }

    flushCartRefresh() {
      if (!this.connected || !this.cartRefreshQueued || this.preparing || this.applying || this.addition ||
        this.document.hidden || pageLeaving) return;
      this.cartRefreshQueued = false;
      this.schedule(true);
    }

    observeFormContext() {
      if (!this.connected || pageLeaving) return;
      const key = revisionKey(this.current() || {});
      if (this.formContextKey !== undefined && key !== this.formContextKey) {
        this.invalidate();
        this.schedule();
      }
      this.formContextKey = key;
      this.contextTimer = root.setTimeout(() => this.observeFormContext(), 500);
    }

    mainForm() {
      const data = this.dataset;
      if (!data.sectionId || data.formId !== 'product-form-' + data.sectionId ||
        !this.productInfo || this.productInfo.id !== 'MainProduct-' + data.sectionId ||
        this.productInfo.dataset.section !== data.sectionId ||
        this.productInfo.dataset.productId !== data.productId || !id(data.productId)) return null;
      const forms = [...(this.document?.querySelectorAll('form[id]') || [])].filter((form) => form.getAttribute('id') === data.formId);
      return forms.length === 1 && this.productInfo.contains(forms[0]) ? forms[0] : null;
    }

    readForm() {
      const form = this.mainForm();
      if (!form?.elements?.namedItem) return {formKnown: false};
      const control = (name) => {
        const field = form.elements.namedItem(name);
        return field && typeof field.value === 'string' && !field.disabled && field.validity?.valid !== false &&
          ['INPUT', 'SELECT'].includes(field.tagName) && (!field.form || field.form === form) ? field : null;
      };
      const variant = control('id');
      const quantity = control('quantity');
      const plan = control('selling_plan');
      const quantityMissing = form.elements.namedItem('quantity') == null;
      const planMissing = form.elements.namedItem('selling_plan') == null;
      const properties = propertiesFromForm(form);
      if (properties === null) return {formKnown: false};
      return {formKnown: true, formVariantId: variant?.value,
        quantity: quantity?.value ?? (quantityMissing && integer(this.dataset.quantityMin) === 1 ? 1 : null),
        sellingPlan: plan?.value ?? (planMissing && yes(this.dataset.oneTime) ? '' : null), ...(properties.length ? {properties} : {})};
    }

    current() {
      return evaluate({...this.dataset, ...this.readForm(), pending: this.pending}).context;
    }

    preserveLayout() {
      if (this.hidden || !this.priceHost?.style?.setProperty) return;
      let rect = this.priceHost.getBoundingClientRect?.();
      if (!(rect?.width > 0 && rect.height > 0)) return;
      if (this.layoutWidth !== rect.width) {
        this.priceHost.style.removeProperty('--alvaura-offer-block-size');
        this.layoutHeight = 0;
        rect = this.priceHost.getBoundingClientRect();
      }
      this.layoutWidth = rect.width;
      this.layoutHeight = Math.max(this.layoutHeight || 0, rect.height);
      this.priceHost.style.setProperty('--alvaura-offer-block-size', this.layoutHeight + 'px');
    }

    invalidate() {
      this.preserveLayout();
      this.sequence = (this.sequence || 0) + 1;
      this.selectedOffer = null;
      root.clearTimeout(this.expiryTimer);
      this.hidden = true;
      const estimate = this.querySelector('[data-first-order-estimate]');
      const badge = this.querySelector('[data-first-order-badge]');
      if (estimate) estimate.hidden = true;
      if (badge) badge.hidden = true;
      if (this.priceHost) delete this.priceHost.dataset.firstOrderEstimate;
    }

    nativePath(name) {
      const prefix = root.Shopify?.routes?.root || '/';
      if (!/^\/(?:[a-zA-Z-]+\/)?$/.test(prefix)) throw new Error('INVALID_NATIVE_ROUTE');
      return prefix + 'cart' + name;
    }

    async nativeCart() {
      const response = await request(this.nativePath('.js'), {credentials: 'same-origin', headers: {Accept: 'application/json'}});
      if (!response.ok) throw new Error('NATIVE_CART_UNAVAILABLE');
      return response.json();
    }

    async refreshIdentity(sequence = this.sequence) {
      const context = this.current();
      if (!context) return false;
      const identity = this.identitySequence = (this.identitySequence || 0) + 1;
      const target = new root.URL(root.location.pathname, root.location.origin);
      target.searchParams.set('section_id', this.dataset.sectionId);
      target.searchParams.set('variant', context.variantId);
      if (context.sellingPlanId) target.searchParams.set('selling_plan', context.sellingPlanId);
      const response = await request(target.pathname + target.search, {credentials: 'same-origin'});
      if (!response.ok) return false;
      const html = new root.DOMParser().parseFromString(await response.text(), 'text/html');
      const candidates = [...html.querySelectorAll('alvaura-first-order-offer')].filter((node) =>
        ['productId', 'sectionId', 'blockId', 'formId', 'variantId'].every((key) => node.dataset[key] === this.dataset[key]));
      if (!this.connected || this.sequence !== sequence || this.identitySequence !== identity || candidates.length !== 1 ||
        revisionKey(this.current() || {}) !== revisionKey(context)) return false;
      const incoming = candidates[0];
      const incomingHost = incoming.closest('product-price');
      if (!incomingHost || ['productId', 'blockId'].some(key => incomingHost.dataset[key] !== this.priceHost?.dataset[key])) return false;
      const nativePrices = this.priceHost.querySelectorAll(':scope > .price-product-container');
      const incomingPrices = incomingHost.querySelectorAll(':scope > .price-product-container');
      if (nativePrices.length !== 1 || incomingPrices.length !== 1) return false;
      this.copySource(incoming);
      nativePrices[0].replaceWith(incomingPrices[0].cloneNode(true));
      return Boolean(this.current());
    }

    copySource(incoming, replaceContent = true) {
      for (const key of keys) {
        if (incoming.dataset[key] === undefined) delete this.dataset[key];
        else this.dataset[key] = incoming.dataset[key];
      }
      if (replaceContent) this.innerHTML = incoming.innerHTML;
    }

    async refresh(verifyIdentity = false, allowRecovery = true) {
      if (!this.connected || this.applying || this.preparing || this.addition || this.document.hidden || pageLeaving) return;
      this.cartRefreshQueued = false;
      this.invalidate();
      const sequence = this.sequence;
      try {
        if (recovery) await waitForRecovery();
        if (verifyIdentity && !await this.refreshIdentity(sequence)) return;
        if (!this.connected || this.sequence !== sequence || pageLeaving) return;
        const formContext = this.current();
        if (!formContext || !this.sourceValid || !this.priceHost) {
          this.dataset.offerState = 'form-context-unconfirmed';
          return;
        }
        if (!getSession(this.dataset)) {
          if (recovery) await waitForRecovery();
          this.dataset.offerState = 'engine-unavailable';
          return;
        }
        const actualCart = await this.nativeCart();
        if (!this.connected || this.sequence !== sequence || pageLeaving) return;
        const context = purchaseContext(actualCart, formContext);
        if (!context) {
          this.dataset.offerState = 'cart-context-unconfirmed';
          return;
        }
        const offer = await quoteOffer(this.dataset, context);
        if (!offer || !this.connected || this.sequence !== sequence || pageLeaving) return;
        const latestCart = await this.nativeCart();
        if (!this.connected || this.sequence !== sequence || revisionKey(this.current() || {}) !== revisionKey(formContext) ||
          (context.basket ? basketShape(nativeBasket(latestCart, formContext)) !== basketShape(nativeBasket(actualCart, formContext)) :
            matchingQuantity(latestCart, formContext) !== matchingQuantity(actualCart, formContext)) || !sameCodes(actualCart, latestCart)) return;
        if (context.basket ? !basketCodesPreserved(latestCart, offer) : !preservesCodes(latestCart, offer.code)) {
          this.dataset.offerState = 'existing-code-preserved';
          return;
        }
        this.renderOffer(context, offer, formContext);
        this.dataset.offerState = this.hidden ? 'no-confirmed-offer' : 'quoted';
      } catch (error) {
        if (this.sequence === sequence) {
          this.dataset.offerState = /^[A-Z_]{1,50}$/.test(error?.code || '') ? 'quote-' + error.code : 'quote-unconfirmed';
          this.invalidate();
          if (allowRecovery && error?.quoteRecovered === true && this.connected && !pageLeaving && !this.document.hidden) {
            if (this.preparing || this.applying || this.addition) this.cartRefreshQueued = true;
            else await this.refresh(true, false);
          }
        }
      } finally {
        if (this.connected && this.sequence === sequence) this.expiryTimer = root.setTimeout(() => {
          this.invalidate();
          if (!this.document.hidden) this.refresh(true);
        }, 60000);
      }
    }

    renderOffer(context, offer, formContext = this.current()) {
      const result = evaluate({...this.dataset, ...this.readForm(), quantity: context.quantity, basket: context.basket, pending: this.pending}, offer);
      const estimate = this.querySelector('[data-first-order-estimate]');
      const badge = this.querySelector('[data-first-order-badge]');
      const amount = this.querySelector('[data-offer-amount]');
      const label = this.querySelector('[data-offer-label]');
      if (!result.showBadge || !estimate || !badge || !amount || !label) return;
      const welcome = normalizedCode(offer.code) === normalizedCode(this.dataset.ruleCode);
      if (welcome && (!Number.isSafeInteger(offer.usdSavingCents) || offer.usdSavingCents <= 0 ||
        !this.dataset.welcomeLabel?.includes('__amount__'))) return;
      let text = welcome ? this.dataset.welcomeLabel.replace('__amount__', formatMoney(offer.usdSavingCents, 'USD', true)) : offer.label;
      if (!welcome && offer.publicPlanOffer) {
        if (!this.dataset.planSavingsLabel?.includes('__amount__')) return;
        text = this.dataset.planSavingsLabel.replace('__amount__', formatMoney(offer.basket ? offer.badgeSavingCents : offer.subtotalCents - offer.totalCents, offer.currency));
      }
      if (typeof text !== 'string' || !text.trim()) return;
      label.textContent = text;
      amount.textContent = result.showEstimate ? formatMoney(offer.totalCents, offer.currency) : '';
      this.hidden = false;
      badge.hidden = false;
      estimate.hidden = !result.showEstimate;
      if (result.showEstimate) this.priceHost.dataset.firstOrderEstimate = 'true';
      this.preserveLayout();
      this.selectedOffer = {context, offer, key: revisionKey(context), formKey: revisionKey(formContext || {}), expiresAt: Date.now() + 60000};
    }

    schedule(verifyIdentity = false) {
      this.identityQueued = Boolean(this.identityQueued || verifyIdentity);
      root.clearTimeout(this.inputTimer);
      this.inputTimer = root.setTimeout(() => {
        const verify = this.identityQueued;
        this.identityQueued = false;
        this.refresh(verify);
      }, 200);
    }

    syncVariant(event) {
      const detail = event?.detail;
      const data = detail?.data;
      if (String(data?.productId || '') !== this.dataset.productId ||
        ![this.dataset.sectionId, this.productInfo?.id].includes(detail?.sourceId)) return;
      this.pending = true;
      this.invalidate();
      const variantId = detail?.resource?.id == null ? null : String(detail.resource.id);
      const candidates = [...(data?.html?.querySelectorAll?.('alvaura-first-order-offer') || [])].filter((node) =>
        ['productId', 'sectionId', 'blockId', 'formId'].every((key) => node.dataset?.[key] === this.dataset[key]));
      if (!id(variantId) || candidates.length !== 1 || candidates[0].dataset.variantId !== variantId ||
        this.readForm().formVariantId !== variantId) {
        this.sourceValid = false;
        return;
      }
      this.copySource(candidates[0]);
      this.sourceValid = true;
      this.pending = false;
      this.schedule();
    }

    submittedContext(formData) {
      const context = this.current();
      const properties = propertiesFromData(formData);
      return context && String(formData.get('id')) === context.variantId &&
        Number(formData.get('quantity') || 1) === context.quantity &&
        String(formData.get('selling_plan') || '') === context.sellingPlanId && properties !== null &&
        JSON.stringify(properties) === JSON.stringify(context.properties || []) ? context : null;
    }

    async prepareAdd(formData) {
      if (pageLeaving || this.preparing || this.applying || this.addition) return null;
      const formContext = this.submittedContext(formData);
      if (!formContext) return null;
      this.preparing = true;
      try {
        const actualCart = await this.nativeCart();
        const context = purchaseContext(actualCart, formContext);
        if (!context || revisionKey(this.submittedContext(formData) || {}) !== revisionKey(formContext)) return null;
        const ticket = {formKey: revisionKey(formContext), context, existingQuantity: matchingQuantity(actualCart, formContext),
          codes: cartCodes(actualCart), expiresAt: Date.now() + 60000};
        this.addition = ticket;
        return ticket;
      } catch (_) { return null; }
      finally {
        this.preparing = false;
        this.flushCartRefresh();
      }
    }

    finishAdd(ticket) {
      if (ticket && this.addition === ticket) {
        this.addition = null;
        this.invalidate();
        this.cartRefreshQueued = false;
        this.schedule(true);
      }
      this.flushCartRefresh();
    }

    async applyAfterAdd(formData, sections = [], ticket) {
      const saved = this.selectedOffer;
      const formContext = this.submittedContext(formData);
      const context = ticket?.context;
      if (pageLeaving || !this.connected || this.applying || !formContext || !context || ticket !== this.addition || ticket.expiresAt <= Date.now() ||
        ticket.formKey !== revisionKey(formContext)) return null;
      this.applying = true;
      let phase = 'identity';
      try {
        if (!await this.refreshIdentity() || ticket.formKey !== revisionKey(this.submittedContext(formData) || {})) {
          this.dataset.offerState = 'identity-unconfirmed';
          this.invalidate();
          return null;
        }
        phase = 'cart';
        const actualCart = await this.nativeCart();
        if (!(context.basket ? basketMatches(actualCart, context) && sameCodeNames(actualCart, {discount_codes: ticket.codes}) :
          cartMatches(actualCart, context) && sameCodes(actualCart, {discount_codes: ticket.codes}))) {
          this.dataset.offerState = 'cart-context-unconfirmed';
          this.invalidate();
          return null;
        }
        if (context.basket) return await this.applyBasketAfterAdd(formData, sections, ticket, actualCart);
        phase = 'quote';
        const offer = saved && saved.expiresAt > Date.now() && saved.key === revisionKey(context) && saved.formKey === ticket.formKey
          ? saved.offer : await quoteOffer(this.dataset, context);
        if (!offer || pageLeaving || !this.connected || ticket !== this.addition || ticket.expiresAt <= Date.now() || ticket.formKey !== revisionKey(this.submittedContext(formData) || {}) ||
          !evaluate({...this.dataset, ...this.readForm(), quantity: context.quantity, pending: this.pending}, offer).showBadge ||
          !preservesCodes(actualCart, offer.code)) {
          this.dataset.offerState = 'offer-unconfirmed';
          this.invalidate();
          return null;
        }
        if (!offer.code) {
          const confirmed = actualCart.total_price === offer.totalCents;
          this.dataset.offerState = confirmed ? 'native-offer-confirmed' : 'native-offer-unconfirmed';
          this.dataset.offerNativeTotal = String(actualCart.total_price);
          if (!confirmed) this.invalidate();
          return {confirmed, sections: null};
        }
        if (!code(offer.code)) return null;
        const currentCodes = cartCodes(actualCart);
        if (currentCodes.some((entry) => normalizedCode(entry.code) === normalizedCode(offer.code) && entry.applicable)) {
          const confirmed = actualCart.total_price === offer.totalCents;
          this.dataset.offerState = confirmed ? 'applied' : 'application-unconfirmed';
          if (!confirmed) this.invalidate();
          return {confirmed, sections: null};
        }
        const currentCart = await this.nativeCart();
        if (pageLeaving || !this.connected || ticket !== this.addition || ticket.expiresAt <= Date.now() || !cartMatches(currentCart, context) || !sameCodes(actualCart, currentCart) ||
          ticket.formKey !== revisionKey(this.submittedContext(formData) || {})) {
          this.dataset.offerState = 'cart-context-changed';
          this.invalidate();
          return null;
        }
        const body = {discount: offer.code};
        const safeSections = sections.filter((value) => typeof value === 'string' && /^[A-Za-z0-9_-]+$/.test(value)).slice(0, 5);
        if (safeSections.length) {
          body.sections = safeSections;
          body.sections_url = root.location.pathname;
        }
        phase = 'application';
        const response = await request(this.nativePath('/update.js'), {method: 'POST', credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)});
        if (!response.ok) {
          this.dataset.offerState = 'application-rejected';
          this.dataset.offerResponseStatus = String(response.status || 0);
          this.invalidate();
          return null;
        }
        const updated = await response.json();
        const codes = cartCodes(updated);
        this.dataset.offerResponseStatus = String(response.status || 0);
        this.dataset.offerCartMatched = String(cartMatches(updated, context));
        this.dataset.offerCodeApplied = String(Boolean(codes?.some((entry) =>
          normalizedCode(entry.code) === normalizedCode(offer.code) && entry.applicable)));
        this.dataset.offerNativeTotal = Number.isSafeInteger(updated.total_price) ? String(updated.total_price) : '';
        const confirmed = cartMatches(updated, context) && codes?.some((entry) =>
          normalizedCode(entry.code) === normalizedCode(offer.code) && entry.applicable) &&
          updated.total_price === offer.totalCents;
        this.dataset.offerState = confirmed ? 'applied' : 'application-unconfirmed';
        if (!confirmed) this.invalidate();
        return {confirmed: Boolean(confirmed), sections: updated.sections};
      } catch (error) {
        const failure = ['AbortError', 'TypeError', 'SyntaxError'].includes(error?.name) ? error.name : 'unconfirmed';
        this.dataset.offerState = phase + '-' + failure;
        this.invalidate();
        return null;
      } finally {
        this.applying = false;
        this.flushCartRefresh();
      }
    }

    async applyBasketAfterAdd(formData, sections, ticket, actualCart) {
      const context = {...ticket.context, basket: {...ticket.context.basket, codes: cartCodes(actualCart)}};
      const offer = await quoteOffer(this.dataset, context);
      if (!offer || pageLeaving || !this.connected || ticket !== this.addition || ticket.expiresAt <= Date.now() || ticket.formKey !== revisionKey(this.submittedContext(formData) || {}) ||
        !evaluate({...this.dataset, ...this.readForm(), basket: context.basket, pending: this.pending}, offer).showBadge || !basketCodesPreserved(actualCart, offer)) {
        this.invalidate();
        this.dataset.offerState = 'offer-unconfirmed';
        return null;
      }
      const current = await this.nativeCart();
      if (pageLeaving || !this.connected || ticket !== this.addition || ticket.expiresAt <= Date.now() || !basketMatches(current, context) || !sameCodes(actualCart, current) || ticket.formKey !== revisionKey(this.submittedContext(formData) || {})) {
        this.invalidate();
        this.dataset.offerState = 'cart-context-changed';
        return null;
      }
      const desired = offer.applyCodes.map(normalizedCode).sort();
      const existing = cartCodes(current).map(entry => normalizedCode(entry.code)).sort();
      const confirm = cart => basketMatches(cart, context) && cart.total_price === offer.totalCents &&
        JSON.stringify(cartCodes(cart)?.map(entry => normalizedCode(entry.code)).sort()) === JSON.stringify(desired) &&
        basketCodesPreserved(actualCart, {applyCodes: cartCodes(cart)?.map(entry => entry.code), codeStates: cartCodes(cart)}) &&
        offer.codeStates.every(entry => !entry.applicable || cartCodes(cart)?.some(candidate => candidate.applicable && normalizedCode(candidate.code) === normalizedCode(entry.code)));
      if (JSON.stringify(desired) === JSON.stringify(existing)) {
        const confirmed = confirm(current);
        if (!confirmed) this.invalidate();
        this.dataset.offerState = confirmed ? 'native-offer-confirmed' : 'native-offer-unconfirmed';
        return {confirmed, sections: null};
      }
      const body = {discount: offer.applyCodes.join(',')};
      const safeSections = sections.filter(value => typeof value === 'string' && /^[A-Za-z0-9_-]+$/.test(value)).slice(0, 5);
      if (safeSections.length) { body.sections = safeSections; body.sections_url = root.location.pathname; }
      const response = await request(this.nativePath('/update.js'), {method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)});
      if (!response.ok) { this.invalidate(); this.dataset.offerState = 'application-rejected'; return null; }
      const updated = await response.json();
      const confirmed = Boolean(confirm(updated));
      this.dataset.offerNativeTotal = Number.isSafeInteger(updated.total_price) ? String(updated.total_price) : '';
      this.dataset.offerState = confirmed ? 'applied' : 'application-unconfirmed';
      if (!confirmed) {
        this.invalidate();
        const restored = await this.restoreBasketCodes(formData, sections, ticket, actualCart, updated, desired);
        if (restored) return restored;
      }
      return {confirmed, sections: updated.sections};
    }

    async restoreBasketCodes(formData, sections, ticket, before, updated, desired) {
      const context = ticket.context;
      const beforeCodes = cartCodes(before);
      const updatedCodes = cartCodes(updated);
      const owned = () => !pageLeaving && this.connected && ticket === this.addition && ticket.expiresAt > Date.now() &&
        ticket.formKey === revisionKey(this.submittedContext(formData) || {});
      const lost = beforeCodes?.some(entry => entry.applicable && !updatedCodes?.some(candidate => candidate.applicable && normalizedCode(candidate.code) === normalizedCode(entry.code)));
      const worse = Number.isSafeInteger(before.total_price) && Number.isSafeInteger(updated.total_price) && updated.total_price > before.total_price;
      if (!owned() || !beforeCodes || !updatedCodes || (!lost && !worse) || !basketMatches(updated, context) ||
        JSON.stringify(updatedCodes.map(entry => normalizedCode(entry.code)).sort()) !== JSON.stringify(desired)) return null;
      const latest = await this.nativeCart();
      if (!owned() || !basketMatches(latest, context) || !sameCodes(updated, latest) || latest.total_price !== updated.total_price) return null;
      const body = {discount: beforeCodes.map(entry => entry.code).join(',')};
      const safeSections = sections.filter(value => typeof value === 'string' && /^[A-Za-z0-9_-]+$/.test(value)).slice(0, 5);
      if (safeSections.length) { body.sections = safeSections; body.sections_url = root.location.pathname; }
      const response = await request(this.nativePath('/update.js'), {method: 'POST', credentials: 'same-origin', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify(body)});
      if (!response.ok) { this.dataset.offerState = 'restoration-unconfirmed'; return {confirmed: false, restored: false, sections: updated.sections}; }
      const restored = await response.json();
      const restoredCodes = cartCodes(restored);
      const valid = basketMatches(restored, context) && restored.total_price === before.total_price && sameCodeNames(before, restored) &&
        beforeCodes.every(entry => !entry.applicable || restoredCodes?.some(candidate => candidate.applicable && normalizedCode(candidate.code) === normalizedCode(entry.code)));
      this.dataset.offerState = valid ? 'restored-original-codes' : 'restoration-unconfirmed';
      return {confirmed: false, restored: Boolean(valid), sections: restored.sections || updated.sections};
    }
  }

  if (root?.addEventListener) {
    root.addEventListener('pagehide', () => {
      pageLeaving = true;
      const owned = session;
      disposal = Promise.resolve(owned?.dispose()).catch(() => null).finally(() => {
        if (session === owned) {
          session = null;
          sessionConfiguration = null;
        }
      });
    });
    root.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      Promise.resolve(disposal).then(() => {
        pageLeaving = false;
        root.document.querySelectorAll('alvaura-first-order-offer').forEach((element) => element.onActivity?.());
      });
    });
  }
  return {evaluate, matchingQuantity, purchaseContext, cartMatches, cartCodes, preservesCodes, nativeBasket, basketMatches, propertiesFromData, formatMoney, AlvauraFirstOrderOffer};
});
